// Речь → явные недопродажи в ₽ (Алексей 28.10, #3190): чего не было по разговорам («нет товара»),
// сколько это стоит по ценам из чеков и чем кончилось по чеку этого разговора — ушёл, взял дешевле,
// взял другое. Расчёт — ops_tasks/api/lost_sales.py. Показываем, только если подключены чеки.
import React, { useState } from 'react';
import useSWR from 'swr';

import { ChevronDown, Info } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Tooltip } from '@/components/ui/tooltip';
import { getLostSales, type LostOutcome, type LostSaleRowDto } from '@/api/receipts';
import { useZoneFeatures } from '@/hooks/useZoneFeatures';
import { money } from '@/lib/finance';
import { cn } from '@/lib/utils';

const rub = (n: number) => money(`${Math.round(n).toLocaleString('ru-RU')} ₽`);
const OUTCOME: Record<LostOutcome, string> = {
  left: 'ушёл без покупки',
  downgraded: 'взял дешевле',
  other: 'взял другое',
  replaced: 'заменили без потери',
  no_receipt: 'чек не нашли',
};
const REASON: Record<string, string> = { sold_out: 'кончился', not_delivered: 'не привезли / не приготовили', absent: 'нет', not_sold_here: 'не продаём' };
const when = (iso: string) => new Date(iso).toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

function Row({ r, onOpen }: { r: LostSaleRowDto; onOpen: (id: number) => void }) {
  return (
    <button type="button" onClick={() => onOpen(r.conversation_id)}
      className="grid w-full grid-cols-[1fr_auto] gap-x-3 rounded-md px-2 py-1.5 text-left text-xs hover:bg-muted/60">
      <div className="min-w-0">
        <div className="truncate"><span className="font-medium">{r.asked || r.item}</span>
          {r.item && r.item.toLowerCase() !== r.asked.toLowerCase() && <span className="text-muted-foreground"> · в чеках «{r.item}»{r.price ? `, ${rub(r.price)}` : ''}</span>}
        </div>
        <div className="truncate text-muted-foreground">
          {when(r.started_at)}{r.reason ? ` · ${REASON[r.reason]}` : ''} · {OUTCOME[r.outcome]}{r.substitute ? `: «${r.substitute}» за ${rub(r.substitute_price ?? 0)}` : ''}{r.answer ? ` · ответ: ${r.answer}` : ''}
        </div>
      </div>
      <div className={cn('self-center whitespace-nowrap tabular-nums', r.lost_rub ? 'font-medium' : 'text-muted-foreground')}>
        {r.lost_rub ? `−${rub(r.lost_rub)}` : r.outcome === 'no_receipt' && r.price ? `≈ ${rub(r.price)}?` : '—'}
      </div>
    </button>
  );
}

export function LostSalesCard({ zoneId, from, to, onOpen }: { zoneId: string; from: string; to: string; onOpen: (id: number) => void }) {
  const { features } = useZoneFeatures(zoneId);
  const on = !!(features?.receipts?.enabled && features.receipts.has_content);
  const { data } = useSWR(on ? ['speech:lost-sales', zoneId, from, to] : null,
    () => getLostSales(zoneId, from, to).then((r) => r.data), { revalidateOnFocus: false });
  const [all, setAll] = useState(false);
  // Компактно (Алексей 03.11 #3545: «слишком большой блок»): строка с суммой и топом позиций, пояснение —
  // в подсказке, сами случаи — по кнопке.
  const [open, setOpen] = useState(false);
  if (!on || !data || (!data.rows.length && !(data.upsell?.upsold_n || data.upsell?.recommended_n))) return null;
  const shown = data.rows.filter((r) => all || r.lost_rub > 0);
  const counts = (['left', 'downgraded', 'other', 'replaced', 'no_receipt'] as LostOutcome[]).filter((k) => data.by_outcome[k]);
  const hint = (
    <span className="block max-w-80 text-xs leading-snug">
      Гость просил то, чего не было. Цена — средняя по чекам за 30 дней; исход — по чеку этого разговора:
      {' '}{counts.map((k, i) => <span key={k}>{i ? ', ' : ''}{OUTCOME[k]} — {data.by_outcome[k]}</span>)}.
      Добавки (молоко, сироп) — по цене добавки.
      {data.by_reason && (
        <> Почему не было (по ответу сотрудника): {Object.entries(data.by_reason).map(([k, v], i) => <span key={k}>{i ? ', ' : ''}{REASON[k] ?? k} — {v}</span>)}.</>
      )}
      {data.maybe_rub > 0 && <> Ещё до {rub(data.maybe_rub)} — случаи, где чек к разговору не нашли; в сумму не входят.</>}
    </span>
  );
  return (
    <Card>
      <CardContent className="space-y-1.5 px-4 py-2.5">
        {/* На вкладке «Недопродажи» отдельной секцией «По голосу», рядом — явные допродажи (Алексей 03.11 #3567) */}
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">По голосу</div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <div className="text-sm font-medium">Явные недопродажи</div>
          <div className="text-lg font-semibold tabular-nums">≈ {rub(data.total_lost_rub)}</div>
          <Tooltip content={hint}><Info className="size-3.5 text-muted-foreground" aria-label="Как считаем" /></Tooltip>
          {data.upsell && data.upsell.upsold_n + data.upsell.recommended_n > 0 && (
            <Tooltip content={<span className="block max-w-72 text-xs leading-snug">
              Позиции в чеках, которые кассир прямо предложил, и гость согласился: {data.upsell.upsold_n} шт на {rub(data.upsell.upsold_rub)}
              {data.upsell.recommended_n ? `; ещё после рекомендации — ${data.upsell.recommended_n} шт на ${rub(data.upsell.recommended_rub)}` : ''}.
              Считаем по {data.upsell.linked} чекам, которые удалось связать с разговором.
            </span>}>
              <div className="flex items-baseline gap-1.5 border-l pl-3">
                <span className="text-sm font-medium">Явные допродажи</span>
                <span className="text-lg font-semibold tabular-nums text-emerald-700 dark:text-emerald-400">≈ {rub(data.upsell.upsold_rub + data.upsell.recommended_rub)}</span>
              </div>
            </Tooltip>
          )}
          {data.by_item.filter((b) => b.lost_rub > 0).slice(0, 5).map((b) => (
            <span key={b.item ?? b.asked} className="rounded-full bg-muted px-2 py-0.5 text-xs">
              {b.item ?? b.asked} <span className="text-muted-foreground">×{b.count} · {rub(b.lost_rub)}</span>
            </span>
          ))}
          <button type="button" className="ml-auto inline-flex items-center gap-0.5 text-xs text-primary hover:underline" onClick={() => setOpen((v) => !v)}>
            {open ? 'Скрыть случаи' : `Случаи (${data.rows.length})`}
            <ChevronDown className={cn('size-3.5 transition-transform', open && 'rotate-180')} />
          </button>
        </div>
        {open && (
          <>
            <div className="max-h-72 overflow-y-auto">
              {shown.slice(0, all ? 300 : 12).map((r, i) => <Row key={`${r.conversation_id}:${i}`} r={r} onOpen={onOpen} />)}
            </div>
            <button type="button" className="text-xs text-primary hover:underline" onClick={() => setAll((v) => !v)}>
              {all ? 'Только с потерей' : `Все случаи «нет товара» (${data.rows.length})`}
            </button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
